"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ConsentManagerImpl = void 0;
const observable_1 = require("../lib/observable/observable");
const service_1 = require("../types/service");
const localstorage_store_1 = require("../lib/store/localstorage-store");
const js_cookie_1 = require("js-cookie");
class ConsentManagerImpl {
  constructor(config, store) {
    this.states = {};
    this.stateChanged = {};
    this.consents = {};
    this.stateSubjects = {};
    this.purposeStateSubjects = {};
    this.consentSubjects = {};
    this.initialOpenSubject = new observable_1.Subject(false);
    this.isConfirmedSubject = new observable_1.Subject(false);
    this.settingsStateSubject = new observable_1.Subject(false);
    this.hasConfigChanged = false;
    this.config = config;
    if (store !== undefined) {
      this.store = store;
    } else {
      this.store = new localstorage_store_1.LocalStorageStore(this.config.storageName);
    }
    Object.keys(this.config.services).map(service => {
      this.consentSubjects[service] = new observable_1.Subject();
      this.stateSubjects[service] = new observable_1.Subject();
      for (const purpose of this.config.services[service].purposes) {
        if (!this.purposeStateSubjects[purpose]) {
          this.purposeStateSubjects[purpose] = new observable_1.Subject();
        }
      }
    });
    this.initialOpenSubject.next(this.config.renderOnLoad);
    this.restoreConsentsFromStore();
  }
  static getInstance(config, store) {
    if (!this.INSTANCE) {
      this.INSTANCE = new ConsentManagerImpl(config, store);
    }
    return this.INSTANCE;
  }
  setDefaultsWith(consents) {
    Object.keys(this.config.services).map(service => {
      var _a, _b;
      this.consents[service] = this.isServiceRequired(service) || ((_a = consents[service]) !== null && _a !== void 0 ? _a : this.isServiceOptOut(service));
      this.states[service] = this.isServiceRequired(service) || ((_b = consents[service]) !== null && _b !== void 0 ? _b : this.isServiceOptOut(service) || this.isServiceDefault(service));
      this.stateChanged[service] = false;
      this.consentSubjects[service].next(this.consents[service]);
      this.stateSubjects[service].next(this.states[service]);
    });
  }
  /**
   * Check if all services have been enabled.
   * @return boolean, true if all service consents are enabled, false if not
   */
  allServicesAccepted() {
    for (const service in this.config.services) {
      // Ignore services with contextual consent only
      if (this.config.services[service].behavior === 'CONTEXT_ONLY') {
        continue;
      }
      if (!this.consents[service]) {
        return false;
      }
    }
    return true;
  }
  saveConsentsToStore() {
    const date = new Date();
    const cookieExpirationInDays = this.allServicesAccepted() ? this.config.cookieExpiresAfterDays : this.config.notConsentedCookieExpiresAfterDays;
    date.setTime(date.getTime() + cookieExpirationInDays * 24 * 60 * 60 * 1000);
    this.store.set(JSON.stringify(this.consents), date);
    this.hasConfigChanged = false;
    this.isConfirmedSubject.next(true);
    this.setSettingsState(false);
  }
  restoreConsentsFromStore() {
    const validConsents = {};
    const consentData = this.store.get();
    if (consentData !== null) {
      const storedConsents = JSON.parse(consentData);
      const storedConsentNames = new Set(Object.keys(storedConsents));
      let complete = true;
      for (const service in this.config.services) {
        validConsents[service] = storedConsents[service] || false;
        // If there is a service that doesn't exist in the stored consent it must have been added after the users consent.
        if (!storedConsentNames.has(service)) {
          complete = false;
          this.hasConfigChanged = true;
        }
      }
      this.isConfirmedSubject.next(complete);
    }
    this.setDefaultsWith(validConsents);
  }
  applyStates() {
    let save = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    for (const service in this.config.services) {
      this.applyState(service, false);
    }
    if (save) {
      this.saveConsentsToStore();
    }
  }
  applyState(serviceName) {
    let save = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    this.stateChanged[serviceName] = false;
    this.consents[serviceName] = this.states[serviceName];
    this.consentSubjects[serviceName].next(this.consents[serviceName]);
    if (!this.consents[serviceName]) {
      const service = this.getServiceByName(serviceName);
      if (service) {
        this._removeServiceCookies(service);
      }
    }
    if (save) {
      this.saveConsentsToStore();
    }
  }
  reset() {
    this.store.delete();
    this.setDefaultsWith({});
    this.hasConfigChanged = false;
    this.isConfirmedSubject.next(false);
    this.initialOpenSubject.next(this.config.renderOnLoad);
    this.setSettingsState(false);
    Object.values(this.purposeStateSubjects).forEach(subject => subject.next(false));
  }
  observeConsent(serviceName) {
    return this.consentSubjects[serviceName];
  }
  observeState(serviceName) {
    return this.stateSubjects[serviceName];
  }
  andCombineObservables(observables) {
    return (0, observable_1.mapObservable)((0, observable_1.mergeObservables)(...observables), this.andCombineStates);
  }
  andCombineStates(states) {
    let res = states.length > 0 ? states[0] : false;
    for (const state of states) {
      if (res === state) {} else {
        res = undefined;
        break;
      }
    }
    return res;
  }
  getPurposeServicesState(purpose) {
    return this.andCombineStates(Object.keys(this.config.services).filter(serviceName => {
      const service = this.config.services[serviceName];
      return service.behavior !== "REQUIRED" && (service.behavior !== 'CONTEXT_ONLY' || this.states[serviceName]) && service.purposes.includes(purpose);
    }).map(serviceName => this.states[serviceName]));
  }
  observePurposeServicesState(purpose) {
    const allStatesObservable = (0, observable_1.mergeObservables)(...Object.keys(this.config.services).filter(serviceName => this.config.services[serviceName].purposes.includes(purpose)).map(serviceName => this.observeState(serviceName)));
    return (0, observable_1.mapObservable)(allStatesObservable, () => this.getPurposeServicesState(purpose));
  }
  observePurposeState(purposeName) {
    return (0, observable_1.mapObservable)((0, observable_1.mergeObservables)(this.observePurposeServicesState(purposeName), this.purposeStateSubjects[purposeName] || new observable_1.Subject(false)), states => {
      if (states[1]) {
        return true;
      } else {
        return states[0];
      }
    });
  }
  observeAllStates() {
    return this.andCombineObservables(this.getAllPurposes().map(purpose => this.observePurposeState(purpose.name)));
  }
  observeIsConfirmed() {
    return this.isConfirmedSubject;
  }
  getUIConfig() {
    return this.config.ui;
  }
  getAllServices() {
    return Object.keys(this.config.services).map(service => {
      return Object.assign({
        name: service
      }, this.config.services[service]);
    });
  }
  setState(serviceName, value) {
    var _a;
    const newValue = value || this.isServiceRequired(serviceName);
    if (newValue === this.states[serviceName]) {
      return;
    }
    this.states[serviceName] = newValue;
    this.stateSubjects[serviceName].next(newValue);
    // since the state has toggled the changed boolean needs to toggle as well (2 times changed means unchanged)
    this.stateChanged[serviceName] = !this.stateChanged[serviceName];
    for (const purpose of ((_a = this.getServiceByName(serviceName)) === null || _a === void 0 ? void 0 : _a.purposes) || []) {
      this.purposeStateSubjects[purpose].next(this.getPurposeServicesState(purpose));
    }
  }
  setAllStates(value) {
    this.getAllPurposes().forEach(purpose => {
      this.setPurposeState(purpose.name, value);
    });
  }
  setPurposeState(purposeName, value) {
    this.purposeStateSubjects[purposeName].next(value);
    for (const serviceName in this.config.services) {
      const service = this.config.services[serviceName];
      if (service.purposes.includes(purposeName)) {
        const newValue = service.behavior === "REQUIRED" || value && (service.behavior !== 'CONTEXT_ONLY' || this.states[serviceName]);
        if (this.states[serviceName] !== newValue) {
          this.setState(serviceName, newValue);
        }
      }
    }
  }
  isServiceRequired(serviceName) {
    const service = this.getServiceByName(serviceName);
    return (service === null || service === void 0 ? void 0 : service.behavior) === 'REQUIRED';
  }
  isServiceDefault(serviceName) {
    const service = this.getServiceByName(serviceName);
    return (service === null || service === void 0 ? void 0 : service.behavior) === 'DEFAULT_ENABLED';
  }
  isServiceOptOut(serviceName) {
    const service = this.getServiceByName(serviceName);
    return (service === null || service === void 0 ? void 0 : service.behavior) === 'OPT_OUT';
  }
  isConfirmed() {
    return this.isConfirmedSubject.value || false;
  }
  hasChanges() {
    for (const serviceName in this.config.services) {
      if (this.stateChanged[serviceName]) {
        return true;
      }
    }
    return false;
  }
  hasConfigChanges() {
    return this.hasConfigChanged;
  }
  getServiceByName(serviceName) {
    return Object.assign({
      name: serviceName
    }, this.config.services[serviceName]);
  }
  _removeServiceCookies(service) {
    const removeCookie = (name, domain, path) => {
      (0, js_cookie_1.remove)(name, {
        domain,
        path
      });
      // if not explicitly set we try to remove the cookie from subdomains as well.
      if (!domain) {
        (0, js_cookie_1.remove)(name, {
          domain: ".".concat(window.location.hostname),
          path
        });
      }
    };
    for (const cookieConfig of service.cookies || []) {
      if ((0, service_1.isSimpleCookieConfig)(cookieConfig)) {
        removeCookie(cookieConfig.name, cookieConfig.domain, cookieConfig.path);
      } else {
        const cookies = Object.keys((0, js_cookie_1.get)());
        const regex = new RegExp(cookieConfig.nameRegex);
        for (const cookie of cookies) {
          if (regex.test(cookie)) {
            removeCookie(cookie, cookieConfig.domain, cookieConfig.path);
          }
        }
      }
    }
  }
  getContextualConsentByName(contextualConsentName) {
    return this.getAllContextualConsents()[contextualConsentName];
  }
  getAllContextualConsents() {
    return this.config.contextualConsents;
  }
  getAllPurposes() {
    const purposes = [];
    console.log(this.config.purposes);
    for (const serviceName in this.config.services) {
      const service = Object.assign({
        name: serviceName
      }, this.config.services[serviceName]);
      for (const purpose of service.purposes) {
        if (purposes.findIndex(p => p.name === purpose) === -1) {
          purposes.push(Object.assign({
            name: purpose
          }, this.config.purposes[purpose]));
        }
      }
    }
    return purposes;
  }
  observeContextualConsent(contextualConsentName) {
    const contextualConsent = this.getContextualConsentByName(contextualConsentName);
    if (!contextualConsent) {
      console.error("[bryn-consent] unknown contextualConsent \"".concat(contextualConsentName, "\""));
      return new observable_1.Subject(false);
    }
    const obs = this.andCombineObservables((contextualConsent.services || []).map(serviceName => this.observeConsent(serviceName)));
    return (0, observable_1.mapObservable)(obs, value => {
      return !!value;
    });
  }
  observeSettingsState() {
    return this.settingsStateSubject;
  }
  observeManagerState() {
    return (0, observable_1.mapObservable)((0, observable_1.mergeObservables)(this.observeIsConfirmed(), this.initialOpenSubject, this.observeSettingsState()), _ref => {
      let [isConfirmed, initialOpen, showSettings] = _ref;
      var _a;
      return (_a = !isConfirmed && initialOpen || showSettings) !== null && _a !== void 0 ? _a : false;
    });
  }
  setSettingsState(value) {
    this.settingsStateSubject.next(value);
  }
  openSettings() {
    if (!this.initialOpenSubject.value) {
      this.render();
    } else {
      this.setSettingsState(true);
    }
  }
  render() {
    this.initialOpenSubject.next(true);
  }
}
exports.ConsentManagerImpl = ConsentManagerImpl;